/**
 * SSO (OIDC) login API.
 *
 * The SSO init endpoint is public (the user is not yet authenticated), so it
 * runs through `userApiClient` with `skipAuth: true` — the same pattern as the
 * password login call. Routing through `userApiClient` guarantees the request
 * is prefixed with `API_BASE_URL` (split-host correctness), never a relative
 * `/api` URL that would hit the frontend host on staging/prod.
 */

import { userApiClient, ApiError } from './client';

/**
 * Result of an SSO init attempt.
 *
 * - `ssoAvailable: true` with a `redirectUrl` → the caller redirects the
 *   browser to the identity provider.
 * - `ssoAvailable: false` → the email's domain has no SSO configured; the
 *   caller should fall back to password login.
 */
export interface SsoInitResponse {
  ssoAvailable: boolean;
  redirectUrl?: string;
}

/**
 * Start the SSO login flow for an email address.
 *
 * @param email    The email whose domain determines the IdP.
 * @param returnTo Optional post-login destination the backend echoes back
 *                 through the IdP round-trip.
 */
export async function initSso(email: string, returnTo?: string): Promise<SsoInitResponse> {
  try {
    return await userApiClient.post<SsoInitResponse>(
      '/api/v1/auth/sso/init',
      { email, returnTo },
      { skipAuth: true },
    );
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('SSO init failed', 0, 'UnknownError', error);
  }
}
